import { Fragment, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { BoxHeader } from "@/components/design/BoxHeader";
import { Donut } from "./result-panel-visuals";
import {
  conditionNumericThreshold,
  type RankingActualDistribution,
  type RankingCriterionInsight,
  type RankingInsights,
} from "./match-result-derive";

/**
 * „Match-Analyse" — the box above a ranking that says what the pool is made of: the hardest MUSS
 * with its pass ring, the criteria most and least often met, what the AI decided, the data it was
 * missing, and one recommendation.
 *
 * <p><b>Why it is its own component (review of spark!285, point 3).</b> It was ~250 lines inside
 * {@code CandidateRankingPanel} — the JSX plus four derived arrays and three small helpers — and the
 * company ranking had no analysis at all. Kevin's decision on 2026-09-09 is that a company result
 * shows what a person result shows: match analysis, Top 5 and the ranking. One implementation, so
 * the two cannot drift the way the thresholds did.
 *
 * <p><b>It takes an already-built {@link RankingInsights} and draws it.</b> The two sides compute
 * that shape from different data — {@code buildRankingInsights} reads a team's fact-skill items,
 * {@code buildCompanyRankingInsights} reads a ranked company's criteria — and neither arithmetic
 * belongs here. What differs beyond the data is how a criterion is NAMED, which is why
 * {@code labelForCondition} is a prop: a person criterion resolves reference ids through the
 * person-side lookups, a company criterion resolves an industry id through its own.
 *
 * <p><b>The radius card and the jump-to-row are person-only and stay optional.</b> A radius insight
 * exists only where a city criterion does, and jumping to a row needs a per-subject DOM anchor. A
 * caller that has neither passes neither, and the box simply has one fewer column.
 */
export function RankingInsightsPanel({
  insights,
  labelForCondition,
  subjectLabel,
  formatActual,
  recommendationContent,
  fulfilledNote = null,
  radiusSlot = null,
}: {
  insights: RankingInsights;
  labelForCondition: (condition: unknown) => string;
  /** What is being ranked, pluralised and localised: "Kandidaten" or "Unternehmen". */
  subjectLabel: string;
  /**
   * One recorded value, in this side's words. Person criteria turn a stored percent into a level
   * name and a duration into years; a company criterion prints an industry name or a headcount. The
   * arithmetic is the same either way, the vocabulary is not — so the caller supplies it.
   */
  /**
   * How ONE recorded value reads, for the value axis under a numeric distribution.
   *
   * Optional, because it is only ever reached through a numeric distribution: a side whose insights
   * carry `distribution: null` everywhere (the company side does — see company-ranking-derive)
   * would be handing over a formatter nothing can call. Returning `null` falls back to the raw
   * value, so a caller only handles the criteria it has a better word for.
   */
  formatActual?: (condition: unknown, actual: string | null) => string | null;
  recommendationContent: ReactNode;
  /** The person side's radius card, or nothing. Its presence also picks the four-column layout. */
  /**
   * An optional note beside the "Top 3 fulfilled" heading.
   *
   * The person ranking puts a tooltip here explaining that person-detail criteria are held out of
   * the analysis distributions. That is a statement about the PERSON side's criterion kinds, and it
   * appeared verbatim over a company list until it moved out here (Kevin, 2026-09-09: "companies
   * are not persons"). The company side passes none: it reports no distributions at all, so it has
   * nothing to hold out of them.
   */
  fulfilledNote?: ReactNode;
  radiusSlot?: ReactNode;
}) {
  const intl = useIntl();
  const hardestMust = insights.hardestMust;
  const topBottlenecks = insights.bottlenecks.slice(0, 3);
  const insightLabel = labelForCondition;
  // `number`, not `number | null`. The nullable signature came over with the extraction and had no
  // caller here: both call sites pass an arithmetic result (`Math.max(0, 1 - average)` and a share
  // that falls back to 0), so the "–" arm was unreachable. The PERSON panel keeps its own copy of
  // this helper, where the null arm IS used — it prints "–" for a score nothing could assess — and
  // that one is deliberately not shared: a box that reports on a whole pool always has a number.
  const formatPercent = (value: number) =>
    intl.formatNumber(value, { style: "percent", maximumFractionDigits: 0 });
  const formatAnalysisActual = (condition: unknown, value: number | string): string => {
    const typename =
      condition && typeof condition === "object" ? (condition as { __typename?: string }).__typename : "";
    if (typeof value === "number") {
      if (typename === "IndustryExperienceCondition" || typename === "ExperienceCondition") {
        const years = Math.round(value * 10) / 10;
        return intl.formatMessage(
          {
            id: "match.result.ranking.yearsValue",
            defaultMessage: "{years, plural, one {# Jahr} other {# Jahre}}",
          },
          { years },
        );
      }
      if (typename === "ExpertiseCondition") {
        // Fachkompetenz als Stufe auf der 7er-Skala statt Prozent. value ist der Storage-Prozent,
        // linear invertiert (toStoragePercent = level * 100/7).
        return intl.formatNumber((value * 7) / 100, { maximumFractionDigits: 1 });
      }
      return intl.formatNumber(value, { maximumFractionDigits: 1 });
    }
    return formatActual?.(condition, value) ?? value;
  };
  const formatDistribution = (distribution: RankingActualDistribution): string | undefined => {
    // Numerische Verteilungen erklaert die Glyphen-Legende der Achse selbst; nur
    // Text-Verteilungen brauchen die detail-Zeile ("haeufigster Wert ...").
    if (distribution.kind === "numeric") return undefined;
    return intl.formatMessage(
      {
        id: "match.result.insights.textDistribution",
        defaultMessage: "häufigster Wert {value} ({count}/{present}) · {distinct} Varianten",
      },
      {
        value: formatAnalysisActual(distribution.condition, distribution.topValue),
        count: distribution.topCount,
        present: distribution.present,
        distinct: distribution.distinctCount,
      },
    );
  };
  // Ohne constraintSummary: die Composition ("exact 1") ist in der Meta-Box Rauschen.
  const clampPercent = (value: number) => Math.max(0, Math.min(100, Math.round(value)));
  // Der Donut zeigt den Anteil, der das K.O.-Kriterium BESTEHT: ein zu 94% gefuellter
  // Ring liest sich als "94% gut", auch wenn daneben "ausgeschlossen" steht.
  const koPassPercent = hardestMust
    ? clampPercent(((hardestMust.total - hardestMust.count) / Math.max(1, hardestMust.total)) * 100)
    : 100;
  // "n nicht bewertbar" haelt die UNKNOWN-Items sichtbar, die aus met/failed und dem
  // Durchschnitt bewusst herausgehalten werden (spark#175) - sonst suggeriert "3/10
  // erfüllt", die restlichen sieben haetten nachweislich nicht geliefert.
  const unknownPart = (item: RankingCriterionInsight) =>
    item.unknownCount > 0
      ? intl.formatMessage(
          { id: "match.result.insights.unknownShort", defaultMessage: "{count} nicht bewertbar" },
          { count: item.unknownCount },
        )
      : null;
  const bottleneckLines = topBottlenecks.map((item, index) => {
    const gap = Math.max(0, 1 - item.averageFulfillment);
    return {
      key: `bottleneck-${index}`,
      label: insightLabel(item.condition),
      sub: [
        intl.formatMessage(
          { id: "match.result.insights.fulfilledShort", defaultMessage: "{met}/{total} erfüllt" },
          { met: item.metCount, total: item.total },
        ),
        unknownPart(item),
      ]
        .filter(Boolean)
        .join(" · "),
      // Bar kodiert die Luecke: groesstes Problem = vollster Balken, passend zur Sortierung.
      value: gap,
      right: intl.formatMessage(
        { id: "match.result.insights.gapShort", defaultMessage: "{value} Lücke" },
        { value: formatPercent(gap) },
      ),
      detail: item.distribution ? formatDistribution(item.distribution) : undefined,
      distribution: item.distribution ?? undefined,
      requiredValue: conditionNumericThreshold(item.condition),
    };
  });
  // Positives Gegenstueck zu den Bottlenecks: welche Kriterien bringt der Pool mit.
  // Zaehlt Erfuellung inkl. AI-Flips und traegt damit auch bei FULL_AI Information,
  // wo die Ist-Wert-Verteilungen mangels harter actual-Werte leer blieben.
  const fulfilledLines = insights.topFulfilled.map((item, index) => {
    const share = item.total > 0 ? item.metCount / item.total : 0;
    const fulfilled = intl.formatMessage(
      { id: "match.result.insights.fulfilledShort", defaultMessage: "{met}/{total} erfüllt" },
      { met: item.metCount, total: item.total },
    );
    const aiPart =
      item.aiMetCount > 0
        ? intl.formatMessage(
            { id: "match.result.insights.aiMetShort", defaultMessage: "davon {count} per KI erkannt" },
            { count: item.aiMetCount },
          )
        : null;
    return {
      key: `fulfilled-${index}`,
      label: insightLabel(item.condition),
      sub: [fulfilled, aiPart, unknownPart(item)].filter(Boolean).join(" · "),
      value: share,
      right: formatPercent(share),
      // Werte-Achse (Median/Ø/SD) bleibt, aber bewusst OHNE requiredValue: das Soll steht
      // in dieser Spalte schon im Label ("mind. 6"), der Soll-Marker waere doppelt. Bei den
      // Bottlenecks traegt der Marker dagegen die Luecken-Aussage.
      detail: item.distribution ? formatDistribution(item.distribution) : undefined,
      distribution: item.distribution ?? undefined,
    };
  });
  const aiLines = insights.ai.criteria.map((item, index) => ({
    key: `ai-${index}`,
    label: insightLabel(item.condition),
    sub: intl.formatMessage(
      // THE SUBJECT NOUN COMES FROM THE CALLER. This box now draws both rankings, and "3 Kandidaten"
      // under a list of companies is simply wrong. `candidateCount` keeps its name — it is the
      // person side's field and renaming it would ripple through both builders — but what it is
      // COUNTING is named by whoever is being ranked.
      { id: "match.result.insights.aiCriterionSubjects", defaultMessage: "{count} {subject}" },
      { count: item.candidateCount, subject: subjectLabel },
    ),
    value: item.reasonCount,
    right: intl.formatMessage(
      { id: "match.result.insights.reasonsShort", defaultMessage: "{count} Reasons" },
      { count: item.reasonCount },
    ),
  }));
  const insightComparisonRows = Array.from(
    { length: Math.max(bottleneckLines.length, fulfilledLines.length, 1) },
    (_, index) => index,
  );

  return (
        <div className="box bg-surface p-5" data-testid="ranking-insights">
          <BoxHeader title={<FormattedMessage id="match.result.insights.header" defaultMessage="Match-Analyse" />} />
          <div
            id="ranking-insight-recommendation"
            className="mt-3 border-l-2 border-accent-decoration bg-accent/5 px-4 py-3"
            data-testid="ranking-insight-recommendation"
          >
            <div className="text-smycles-micro tracking-[0.2em] text-ink-accent">
              <FormattedMessage id="match.result.insights.recommendation" defaultMessage="Empfehlung" />
            </div>
            <div className="mt-1 smycles-field-value max-w-5xl text-ink">{recommendationContent}</div>
          </div>
          {/* Mit Radius-Kriterium eine Vier-Spalten-Zeile mit Karte rechts: reine fr-Spalten ohne
              Mindestbreiten, damit Ist-Werte/Bottlenecks mitschrumpfen statt umzubrechen. */}
          <div
            className={`mt-5 grid grid-cols-1 gap-y-6 border-t border-ink/5 pt-4 ${
              radiusSlot
                ? "gap-x-5 @3xl:grid-cols-[0.9fr_1fr_1fr_1.1fr]"
                : "gap-x-7 @lg:grid-cols-[minmax(16rem,0.85fr)_minmax(18rem,1fr)_minmax(24rem,1.25fr)]"
            }`}
          >
            <div className="min-w-0 space-y-6">
              <div id="ranking-insight-ko" className="min-w-0" data-testid="ranking-insight-hardest-must">
                <div className="text-smycles-micro tracking-[0.2em] text-ink-muted">
                  <FormattedMessage id="match.result.insights.hardestMust" defaultMessage="K.O.-Kriterium" />
                </div>
                {hardestMust ? (
                  <div className="mt-2 flex items-center gap-4">
                    <Donut
                      percent={koPassPercent}
                      label={intl.formatMessage({
                        id: "match.result.insights.koPassing",
                        defaultMessage: "erfüllen",
                      })}
                    />
                    <div className="min-w-0">
                      <div className="smycles-field-value text-ink">{insightLabel(hardestMust.condition)}</div>
                      <div className="mt-0.5 text-smycles-label tabular-nums text-ink-muted">
                        <FormattedMessage
                          id="match.result.insights.failCount"
                          defaultMessage="{failed}/{total} ausgeschlossen"
                          values={{ failed: hardestMust.count, total: hardestMust.total }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-1 text-smycles-meta text-ink-muted">
                    <FormattedMessage
                      id="match.result.insights.noExclusion"
                      defaultMessage="Kein Muss-Kriterium schließt aus."
                    />
                  </div>
                )}
              </div>
              <div id="ranking-insight-ai" className="min-w-0" data-testid="ranking-insight-ai">
                <div className="text-smycles-micro tracking-[0.2em] text-ink-muted">
                  <FormattedMessage id="match.result.insights.aiShare" defaultMessage="AI share" />
                </div>
                <div className="mt-1 smycles-field-value tabular-nums text-ink">
                  <FormattedMessage
                    id="match.result.insights.aiReasons"
                    defaultMessage="{count} AI Reasons"
                    values={{ count: insights.ai.reasonCount }}
                  />
                </div>
                <InsightTextList
                  items={aiLines}
                  empty={<FormattedMessage id="match.result.insights.noAiReasons" defaultMessage="Keine AI Reasons." />}
                />
              </div>
            </div>
            <div
              className={`min-w-0 ${radiusSlot ? "@3xl:col-span-2" : "@lg:col-span-2"}`}
              data-testid="ranking-insight-comparison"
            >
              <div
                className={`grid grid-cols-1 gap-y-3 @lg:gap-y-3 ${
                  radiusSlot
                    ? "@3xl:grid-cols-2 @3xl:gap-x-5"
                    : "@lg:grid-cols-[minmax(18rem,1fr)_minmax(24rem,1.25fr)] @lg:gap-x-7"
                }`}
              >
                <div className="order-1 flex items-center gap-1.5 text-smycles-micro tracking-[0.2em] text-ink-muted @lg:order-none" data-testid="ranking-insight-fulfilled">
                  <FormattedMessage id="match.result.insights.topFulfilled" defaultMessage="Top 3 erfüllte Kriterien" />
                  {fulfilledNote}
                </div>
                <div className="order-5 text-smycles-micro tracking-[0.2em] text-ink-muted @lg:order-none" data-testid="ranking-insight-bottlenecks">
                  <FormattedMessage id="match.result.insights.bottlenecks" defaultMessage="Top 3 Bottlenecks" />
                </div>
                {insightComparisonRows.map((index) => {
                  const bottleneck = bottleneckLines[index];
                  const fulfilled = fulfilledLines[index];
                  return (
                    <Fragment key={index}>
                      <div
                        id={`ranking-insight-fulfilled-${index + 1}`}
                        className={`min-w-0 ${
                          INSIGHT_FULFILLED_ORDER[index] ?? "order-4"
                        } @lg:order-none`}
                      >
                        {fulfilled ? (
                          <InsightBarRow item={fulfilled} max={1} formatActual={formatAnalysisActual} />
                        ) : index === 0 ? (
                          <div className="text-smycles-meta text-ink-muted">
                            <FormattedMessage
                              id="match.result.insights.noFulfilled"
                              defaultMessage="Kein Kriterium wird erfüllt."
                            />
                          </div>
                        ) : null}
                      </div>
                      <div
                        id={`ranking-insight-bottleneck-${index + 1}`}
                        className={`min-w-0 ${
                          INSIGHT_BOTTLENECK_ORDER[index] ?? "order-8"
                        } @lg:order-none`}
                      >
                        {bottleneck ? (
                          <InsightBarRow item={bottleneck} max={1} formatActual={formatAnalysisActual} axisOnly />
                        ) : index === 0 ? (
                          <div className="text-smycles-meta text-ink-muted">
                            <FormattedMessage
                              id="match.result.insights.noBottleneck"
                              defaultMessage="Keine auffälligen Engpässe."
                            />
                          </div>
                        ) : null}
                      </div>
                    </Fragment>
                  );
                })}
              </div>
            </div>
            {/* A SLOT, not a card this panel builds. A radius insight exists only where a CITY
                criterion does — a person-side notion — and rendering it needs a per-person DOM
                anchor to jump to. The person panel passes the finished card; the company panel
                passes nothing and the grid drops to three columns. */}
            {radiusSlot}
          </div>
        </div>
  );
}


const INSIGHT_FULFILLED_ORDER = ["order-2", "order-3", "order-4"];
const INSIGHT_BOTTLENECK_ORDER = ["order-6", "order-7", "order-8"];

function InsightTextList({
  items,
  empty,
}: {
  items: Array<{ key: string; label: string; sub?: string; value: number; right: string }>;
  empty: ReactNode;
}) {
  if (items.length === 0) {
    return <div className="mt-1 text-smycles-meta text-ink-muted">{empty}</div>;
  }
  return (
    <div className="mt-2 space-y-2">
      {items.map((item) => (
        <div key={item.key} className="min-w-0 border-t border-ink/5 pt-2 first:border-t-0 first:pt-0">
          <div className="flex items-baseline justify-between gap-3">
            <div className="smycles-field-value min-w-0 truncate text-ink">{item.label}</div>
            <div className="shrink-0 text-smycles-label tabular-nums text-ink">{item.right}</div>
          </div>
          <div className="mt-0.5 truncate text-smycles-label tabular-nums text-ink-muted">{item.sub}</div>
        </div>
      ))}
    </div>
  );
}

function InsightBarRow({
  item,
  max,
  formatActual,
  axisOnly = false,
}: {
  item: {
    key: string;
    label: string;
    sub?: string;
    value: number;
    right: string;
    detail?: string;
    distribution?: RankingActualDistribution;
    requiredValue?: number | null;
  };
  max: number;
  formatActual?: (condition: unknown, value: number | string) => string;
  // Verteilungs-Achse ersetzt den Anteils-Balken statt ihn zu doppeln (Bottlenecks:
  // die Luecke erzaehlt die Achse ueber den Abstand zum Soll-Marker).
  axisOnly?: boolean;
}) {
  // Achse nur mit Signal: sind alle erfassten Werte 0 (FULL_AI-Matches ohne harten
  // Nachweis, Stufe unbewertet), zeigt "Median 0 · Ø 0" Scheininformation und faellt weg.
  // Bottlenecks (axisOnly) fallen dann auf den Luecken-Balken zurueck.
  const numericDistribution =
    item.distribution?.kind === "numeric" && formatActual && item.distribution.max > 0
      ? item.distribution
      : null;
  const showBar = !(axisOnly && numericDistribution);
  return (
    <div className="min-w-0">
      <div className="smycles-field-value truncate text-ink">{item.label}</div>
      {showBar ? (
        <div className="mt-2">
          <div className="relative h-4">
            <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded bg-ink/10" />
            <div
              className="absolute left-0 top-1/2 h-[3px] -translate-y-1/2 rounded"
              style={{
                width: `${Math.max(3, Math.min(100, (item.value / max) * 100))}%`,
                background: "var(--color-accent)",
              }}
            />
          </div>
        </div>
      ) : null}
      <div className="mt-0.5 flex justify-between gap-3 text-smycles-label tabular-nums text-ink-muted">
        <span className="min-w-0 truncate">{item.sub}</span>
        <span className="shrink-0">{item.right}</span>
      </div>
      {numericDistribution && formatActual ? (
        <NumericDistributionAxis
          distribution={numericDistribution}
          formatActual={formatActual}
          requiredValue={item.requiredValue ?? null}
        />
      ) : null}
      {item.detail ? (
        <div className="mt-0.5 truncate text-smycles-label tabular-nums text-ink-muted" title={item.detail}>
          {item.detail}
        </div>
      ) : null}
    </div>
  );
}

function NumericDistributionAxis({
  distribution,
  formatActual,
  requiredValue = null,
}: {
  distribution: Extract<RankingActualDistribution, { kind: "numeric" }>;
  formatActual: (condition: unknown, value: number | string) => string;
  requiredValue?: number | null;
}) {
  const isExpertise =
    (distribution.condition as { __typename?: string } | null)?.__typename === "ExpertiseCondition";
  // Fachkompetenz: Achse fix ueber die volle Stufenskala (Storage 0..100 % = Stufe 0..7),
  // nicht Daten-Min/Max, damit die Marker-Lage zwischen Kriterien vergleichbar bleibt.
  // Der Soll-Wert weitet die Achse bei Bedarf, damit der Marker nie ausserhalb liegt.
  const axisMin = Math.min(isExpertise ? 0 : distribution.min, requiredValue ?? Infinity);
  const axisMax = Math.max(isExpertise ? 100 : distribution.max, requiredValue ?? -Infinity);
  const span = axisMax - axisMin;
  const position = (value: number) => {
    if (span <= 0) return 50;
    return Math.max(0, Math.min(100, ((value - axisMin) / span) * 100));
  };
  const sdStart = position(distribution.average - distribution.standardDeviation);
  const sdEnd = position(distribution.average + distribution.standardDeviation);
  return (
    <div className="mt-1">
      <div className="relative h-4">
        <div className="absolute inset-x-0 top-1/2 h-[3px] -translate-y-1/2 rounded bg-ink/10" />
        <div
          className="absolute top-1/2 h-3 -translate-y-1/2 rounded bg-accent/15"
          style={{ left: `${sdStart}%`, width: `${Math.max(2, sdEnd - sdStart)}%` }}
        />
        <div
          className="absolute -top-0.5 h-5 w-[2px] -translate-x-1/2 rounded bg-accent"
          style={{ left: `${position(distribution.average)}%` }}
        />
        <div
          className="absolute top-1 h-2 w-2 -translate-x-1/2 rounded-full bg-accent"
          style={{ left: `${position(distribution.median)}%` }}
        />
        {requiredValue != null ? (
          <div
            className="absolute -top-1 -translate-x-1/2"
            style={{ left: `${position(requiredValue)}%` }}
          >
            <div className="h-0 w-0 border-x-[5px] border-x-transparent border-t-[6px] border-t-ink" />
          </div>
        ) : null}
      </div>
      <div className="mt-0.5 flex justify-between text-smycles-micro tabular-nums text-ink-muted">
        <span>{formatActual(distribution.condition, axisMin)}</span>
        <span>{formatActual(distribution.condition, axisMax)}</span>
      </div>
      <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-smycles-label tabular-nums text-ink-muted">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-accent" aria-hidden />
          <FormattedMessage
            id="match.result.insights.medianShort"
            defaultMessage="Median {value}"
            values={{ value: formatActual(distribution.condition, distribution.median) }}
          />
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-3.5 w-[2px] rounded bg-accent" aria-hidden />
          <FormattedMessage
            id="match.result.insights.averageShort"
            defaultMessage="Ø {value}"
            values={{ value: formatActual(distribution.condition, distribution.average) }}
          />
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-4 rounded bg-accent/15 ring-1 ring-accent-decoration/20" aria-hidden />
          <FormattedMessage
            id="match.result.insights.sdShort"
            defaultMessage="SD {value}"
            values={{ value: formatActual(distribution.condition, distribution.standardDeviation) }}
          />
        </span>
        {requiredValue != null ? (
          <span className="inline-flex items-center gap-1">
            <span
              className="inline-block h-0 w-0 border-x-[4px] border-x-transparent border-t-[5px] border-t-ink"
              aria-hidden
            />
            <FormattedMessage
              id="match.result.insights.requiredShort"
              defaultMessage="Soll {value}"
              values={{ value: formatActual(distribution.condition, requiredValue) }}
            />
          </span>
        ) : null}
      </div>
    </div>
  );
}
